import { useNavigation } from '@lumapps/lumx-hooks/hooks/useNavigation';
import type { ListDocumentsParams } from '@lumapps/medias-document/api';
import type { DocumentV1 } from '@lumapps/medias-document/types';

import { useFolderContents } from './useFolderContents';

export interface UseSiteLibraryNavigationParams extends Pick<ListDocumentsParams, 'searchTypes' | 'sortOrder'> {
    /**
     * If `false`, the hook will not fetch any data.
     * Set to `true` when the component using this hook is visible to enable data fetching.
     */
    isEnabled: boolean;
}

/**
 * Manages the state and data fetching for navigating the site library with full breadcrumb support.
 * It uses `useNavigation` to manage the path and `useFolderContents` to fetch data.
 *
 * @param {UseSiteLibraryNavigationParams} params
 */
export const useSiteLibraryNavigation = ({ isEnabled, searchTypes, sortOrder }: UseSiteLibraryNavigationParams) => {
    const {
        path: folderPath,
        currentItem: currentFolder,
        currentItemId: currentFolderId,
        navigateToItem: genericNavigateToItem,
        navigateBackToIndex: navigateBreadcrumb,
        resetNavigation: resetToRootFolder,
    } = useNavigation<DocumentV1>({});

    const mediaList = useFolderContents({ folderId: currentFolderId, isEnabled, searchTypes, sortOrder });

    /**
     * Navigates into a folder. If the provided item is not a folder, this function does nothing.
     *
     * @param {DocumentV1} item The folder to navigate into.
     */
    const navigateToFolder = (item: DocumentV1) => {
        if (!item.isFolder) {
            return;
        }

        genericNavigateToItem({ id: item.id, name: item.name });
    };

    return {
        ...mediaList,
        currentFolder,
        folderPath,
        navigateBreadcrumb,
        navigateToFolder,
        resetToRootFolder,
    };
};
